import React, { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Edit2, Download, FileImage, FileText as FilePdf } from 'lucide-react'
import { getQuotations, createQuotation, updateQuotation } from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { StaggerContainer, StaggerItem, ModalOverlay, ButtonLoader } from './components/StaffAnimations'

const STATUS_COLORS = {
    Draft: 'bg-slate-500/15 text-slate-400',
    Sent: 'bg-blue-500/15 text-blue-400',
    Accepted: 'bg-green-500/15 text-green-400',
    Rejected: 'bg-red-500/15 text-red-400',
    Declined: 'bg-orange-500/15 text-orange-400',
}

// ── Quotation Preview (used for both display and export) ──────────────────────
// White-background printable layout
const normalizeItem = (it) => ({
    name: it.name || it.description || '',
    qty: it.qty ?? it.quantity ?? 1,
    rate: it.rate ?? it.unit_price ?? 0,
    amount: it.amount ?? (it.qty ?? it.quantity ?? 1) * (it.rate ?? it.unit_price ?? 0),
})

function QuotationPreview({ quotation, forwardRef }) {
    const rawItems = quotation?.items
    let parsed = []
    if (Array.isArray(rawItems)) parsed = rawItems
    else if (typeof rawItems === 'string') { try { parsed = JSON.parse(rawItems) } catch {} }
    else if (rawItems && typeof rawItems === 'object') parsed = [rawItems]
    const items = parsed.map(normalizeItem)
    const subtotal = parseFloat(quotation?.subtotal || 0)
    const discount = parseFloat(quotation?.discount || 0)
    const total = parseFloat(quotation?.total || 0)
    const isProforma = !!(quotation?.proforma_number && String(quotation.proforma_number).trim())

    return (
        <div
            ref={forwardRef}
            style={{
                background: '#ffffff',
                color: '#111827',
                fontFamily: 'Inter, Arial, sans-serif',
                width: '794px',          // A4 width at 96dpi
                minHeight: '1123px',
                padding: '48px',
                boxSizing: 'border-box',
            }}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src="/new_logo.png" alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />
                    <div>
                        <p style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: '#111827' }}>Abha Enterprises</p>
                        <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Premium EV Dealership — Kolkata</p>
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '24px', fontWeight: '700', color: '#1d4ed8', margin: 0 }}>{isProforma ? 'PROFORMA' : 'QUOTATION'}</p>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                        {isProforma ? quotation?.proforma_number : quotation?.quotation_number}
                    </p>
                    {isProforma && quotation?.invoice_date && (
                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Invoice date: {quotation.invoice_date}
                        </p>
                    )}
                    {isProforma && quotation?.receipt_no && (
                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Receipt No: {quotation.receipt_no}
                        </p>
                    )}
                    {quotation?.valid_until && (
                        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Valid until: {new Date(quotation.valid_until + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                    )}
                </div>
            </div>

            {/* Divider */}
            <div style={{ height: '2px', background: '#1d4ed8', marginBottom: '28px', borderRadius: '2px' }} />

            {/* Bill To */}
            <div style={{ marginBottom: '28px' }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Bill To</p>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{quotation?.customer_name || '—'}</p>
                {quotation?.customer_phone && <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{quotation.customer_phone}</p>}
                {quotation?.customer_address && <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{quotation.customer_address}</p>}
            </div>

            {/* Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                <thead>
                    <tr style={{ background: '#1d4ed8' }}>
                        <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff', borderRadius: '4px 0 0 0' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff' }}>Description</th>
                        <th style={{ textAlign: 'center', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff' }}>Qty</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff' }}>Rate (₹)</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', fontWeight: '600', color: '#fff', borderRadius: '0 4px 0 0' }}>Amount (₹)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#f9fafb' : '#ffffff' }}>
                            <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{i + 1}</td>
                            <td style={{ padding: '10px 12px', fontSize: '13px', color: '#111827', borderBottom: '1px solid #e5e7eb' }}>{item.name || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>{item.qty}</td>
                            <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                {parseFloat(item.rate || 0).toLocaleString('en-IN')}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '500', color: '#111827', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                {parseFloat(item.amount || 0).toLocaleString('en-IN')}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
                <div style={{ width: '280px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                        <span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span>
                    </div>
                    {discount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '13px', color: '#dc2626', borderBottom: '1px solid #e5e7eb' }}>
                            <span>Discount</span><span>— ₹{discount.toLocaleString('en-IN')}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', fontSize: '16px', fontWeight: '700', color: '#ffffff', background: '#1d4ed8', borderRadius: '8px', marginTop: '6px' }}>
                        <span>{isProforma ? 'Grand Total' : 'Total'}</span>
                        <span>₹{(isProforma && parseFloat(quotation?.grand_total || 0) > 0 ? parseFloat(quotation.grand_total) : total).toLocaleString('en-IN')}</span>
                    </div>
                </div>
            </div>

            {/* Notes */}
            {quotation?.notes && (
                <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '14px', marginBottom: '24px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Notes</p>
                    <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>{quotation.notes}</p>
                </div>
            )}

            {/* Footer */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Created by: {quotation?.created_by || '—'}</p>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>abhaev.store</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>This is a computer-generated {isProforma ? 'proforma' : 'quotation'}</p>
                </div>
            </div>
        </div>
    )
}

// ── Export Modal ──────────────────────────────────────────────────────────────
function ExportModal({ quotation, onClose }) {
    const previewRef = useRef(null)
    const [exporting, setExporting] = useState(null) // 'png' | 'pdf'
    const isProforma = !!(quotation?.proforma_number && String(quotation.proforma_number).trim())
    const docNo = isProforma ? quotation.proforma_number : quotation.quotation_number
    const baseName = docNo || 'document'

    const exportPNG = async () => {
        setExporting('png')
        try {
            const { default: html2canvas } = await import('html2canvas')
            const canvas = await html2canvas(previewRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
            })
            const link = document.createElement('a')
            link.download = `${baseName}.png`
            link.href = canvas.toDataURL('image/png')
            link.click()
            toast.success('PNG exported')
        } catch (e) {
            toast.error('PNG export failed: ' + e.message)
        } finally {
            setExporting(null)
        }
    }

    const exportPDF = async () => {
        setExporting('pdf')
        try {
            const { default: html2canvas } = await import('html2canvas')
            const { default: jsPDF } = await import('jspdf')
            const canvas = await html2canvas(previewRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
            })
            const imgData = canvas.toDataURL('image/png')
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
            const pdfW = pdf.internal.pageSize.getWidth()
            const pdfH = (canvas.height * pdfW) / canvas.width
            pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH)
            pdf.save(`${baseName}.pdf`)
            toast.success('PDF exported')
        } catch (e) {
            toast.error('PDF export failed: ' + e.message)
        } finally {
            setExporting(null)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-5 py-3 bg-slate-900 border-b border-white/8 shrink-0">
                <div className="flex items-center gap-2">
                    <Download size={16} className="text-slate-400" />
                    <span className="text-sm font-medium text-white">{docNo}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={exportPNG}
                        disabled={!!exporting}
                        className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    >
                        <FileImage size={13} />
                        {exporting === 'png' ? 'Exporting...' : 'Export PNG'}
                    </button>
                    <button
                        onClick={exportPDF}
                        disabled={!!exporting}
                        className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    >
                        <FilePdf size={13} />
                        {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
                    </button>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition">
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Preview scrollable area */}
            <div className="flex-1 overflow-auto flex justify-center py-6 px-4">
                <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}>
                    <QuotationPreview quotation={quotation} forwardRef={previewRef} />
                </div>
            </div>
        </div>
    )
}

// ── Create / Edit Modal ───────────────────────────────────────────────────────
function QuotModal({ quotation, profile, isProforma = false, onClose, onSave }) {
    const isEdit = !!quotation?.id
    const datePart = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '').toUpperCase()
    const [form, setForm] = useState({
        quotation_number: quotation?.quotation_number || (isProforma ? '' : `QT-${datePart}-${Math.floor(Math.random() * 9000 + 1000)}`),
        proforma_number: quotation?.proforma_number || (isProforma ? `PF-${datePart}-${Math.floor(Math.random() * 9000 + 1000)}` : ''),
        receipt_no: quotation?.receipt_no || (isProforma ? `PF-${Math.floor(Math.random() * 9000 + 1000)}` : ''),
        invoice_date: quotation?.invoice_date || (isProforma ? new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).split('/').join('-') : ''),
        customer_name: quotation?.customer_name || '',
        customer_phone: quotation?.customer_phone || '',
        customer_address: quotation?.customer_address || '',
        notes: quotation?.notes || '',
        status: quotation?.status || 'Draft',
        valid_until: quotation?.valid_until || '',
        discount: quotation?.discount || 0,
    })
    const [items, setItems] = useState(
        quotation?.items && Array.isArray(quotation.items)
            ? quotation.items.map(normalizeItem)
            : [{ name: '', qty: 1, rate: 0, amount: 0 }]
    )
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const setItem = (i, k, v) => {
        setItems(prev => {
            const next = [...prev]
            next[i] = { ...next[i], [k]: v }
            if (k === 'qty' || k === 'rate') {
                const qty = k === 'qty' ? parseFloat(v) || 0 : parseFloat(next[i].qty) || 0
                const rate = k === 'rate' ? parseFloat(v) || 0 : parseFloat(next[i].rate) || 0
                next[i].amount = qty * rate
            }
            return next
        })
    }

    const addRow = () => setItems(p => [...p, { name: '', qty: 1, rate: 0, amount: 0 }])
    const removeRow = (i) => setItems(p => p.filter((_, j) => j !== i))

    const subtotal = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0)
    const total = subtotal - (parseFloat(form.discount) || 0)

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900 z-10">
                    <h2 className="font-semibold text-white">{isEdit ? (isProforma ? 'Edit Proforma' : 'Edit Quotation') : (isProforma ? 'New Proforma' : 'New Quotation')}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <form onSubmit={e => {
                    e.preventDefault()
                    if (!form.customer_name) { toast.error('Customer name required'); return }
                    onSave({
                        ...form,
                        items,
                        subtotal,
                        total,
                        grand_total: total,
                        discount: parseFloat(form.discount) || 0,
                        created_by: profile?.name || '',
                        created_by_id: profile?.id || '',
                    })
                }} className="p-5 space-y-4">

                    <div className="grid grid-cols-2 gap-3">
                        {!isProforma && (
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Quotation No.</label>
                                <input value={form.quotation_number} onChange={e => set('quotation_number', e.target.value)}
                                    className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                            </div>
                        )}
                        {isProforma && (
                            <>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Proforma No.</label>
                                    <input value={form.proforma_number} onChange={e => set('proforma_number', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 block mb-1">Invoice Date</label>
                                    <input value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)}
                                        className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Valid Until</label>
                            <input type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Customer Name *</label>
                            <input value={form.customer_name} onChange={e => set('customer_name', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Phone</label>
                            <input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-slate-400 block mb-1">Address</label>
                            <input value={form.customer_address} onChange={e => set('customer_address', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                    </div>

                    {/* Items */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-slate-400">Items</label>
                            <button type="button" onClick={addRow} className="text-xs text-blue-400 hover:text-blue-300">+ Add row</button>
                        </div>
                        <div className="space-y-2">
                            <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 px-1">
                                <span className="col-span-5">Description</span>
                                <span className="col-span-2">Qty</span>
                                <span className="col-span-2">Rate (₹)</span>
                                <span className="col-span-2">Amount</span>
                                <span className="col-span-1"></span>
                            </div>
                            {items.map((item, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2">
                                    <input value={item.name} onChange={e => setItem(i, 'name', e.target.value)}
                                        placeholder="Item name"
                                        className="col-span-5 bg-slate-800 border border-white/8 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
                                    <input type="number" min="0" value={item.qty} onChange={e => setItem(i, 'qty', e.target.value)}
                                        className="col-span-2 bg-slate-800 border border-white/8 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
                                    <input type="number" min="0" value={item.rate} onChange={e => setItem(i, 'rate', e.target.value)}
                                        className="col-span-2 bg-slate-800 border border-white/8 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-blue-500" />
                                    <div className="col-span-2 flex items-center px-2 text-sm text-slate-300">
                                        ₹{(parseFloat(item.amount) || 0).toLocaleString('en-IN')}
                                    </div>
                                    <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex items-center justify-center">
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="bg-slate-800 rounded-xl p-4 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-400">
                            <span>Discount</span>
                            <input type="number" min="0" value={form.discount} onChange={e => set('discount', e.target.value)}
                                className="w-24 bg-slate-700 border border-white/8 rounded-lg px-2 py-1 text-sm text-white text-right focus:outline-none" />
                        </div>
                        <div className="flex justify-between font-semibold text-white border-t border-white/5 pt-2">
                            <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Status</label>
                            <select value={form.status} onChange={e => set('status', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500">
                                <option>Draft</option><option>Sent</option><option>Accepted</option><option>Rejected</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Notes</label>
                            <input value={form.notes} onChange={e => set('notes', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500" />
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">Cancel</button>
                        <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-xl text-sm font-medium transition">{isEdit ? 'Update' : 'Create'}</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Quotations() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [modal, setModal] = useState(null)
    const [exportQuot, setExportQuot] = useState(null)

    const { data: quotations = [], isLoading } = useQuery({ queryKey: ['quotations'], queryFn: getQuotations })

    const createMut = useMutation({
        mutationFn: createQuotation,
        onSuccess: () => { qc.invalidateQueries(['quotations']); setModal(null); toast.success('Quotation created') },
        onError: e => toast.error(e.message),
    })
    const updateMut = useMutation({
        mutationFn: ({ id, ...r }) => updateQuotation(id, r),
        onSuccess: () => { qc.invalidateQueries(['quotations']); setModal(null); toast.success('Updated') },
        onError: e => toast.error(e.message),
    })

    return (
        <StaffLayout>
            <div className="space-y-5">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">Quotations & Proformas</h1>
                    <div className="flex gap-2">
                        <button onClick={() => setModal('new-proforma')} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
                            <Plus size={16} /> New Proforma
                        </button>
                        <button onClick={() => setModal('new')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
                            <Plus size={16} /> New Quotation
                        </button>
                    </div>
                </div>

                {/* Status summary */}
                <div className="grid grid-cols-5 gap-3">
                    {['Draft', 'Sent', 'Accepted', 'Rejected'].map(s => (
                        <div key={s} className="bg-slate-900 rounded-xl border border-white/5 p-4">
                            <p className="text-xs text-slate-400">{s}</p>
                            <p className="text-xl font-bold text-white mt-1">{quotations.filter(q => q.status === s).length}</p>
                        </div>
                    ))}
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-4">
                        <p className="text-xs text-purple-400">Proformas</p>
                        <p className="text-xl font-bold text-white mt-1">{quotations.filter(q => q.proforma_number).length}</p>
                    </div>
                </div>

                <div className="bg-slate-900 rounded-xl border border-white/5 overflow-hidden">
                    {isLoading ? (
                        <div className="p-8 text-center text-slate-400">Loading...</div>
                    ) : quotations.length === 0 ? (
                        <div className="p-12 text-center text-slate-500">No quotations yet</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/5">
                                        {['No.', 'Customer', 'Phone', 'Total', 'Status', 'Valid Until', 'Created By', ''].map(h => (
                                            <th key={h} className="text-left text-xs font-medium text-slate-400 px-4 py-3">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {quotations.map(q => (
                                        <tr key={q.id} className="hover:bg-white/2 transition group">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-slate-400 text-xs">{q.proforma_number || q.quotation_number}</span>
                                                    {q.proforma_number && (
                                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400">PROFORMA</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 font-medium text-white">{q.customer_name}</td>
                                            <td className="px-4 py-3 text-slate-400">{q.customer_phone}</td>
                                            <td className="px-4 py-3 font-medium text-white">₹{parseFloat(q.total || 0).toLocaleString('en-IN')}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-1 rounded-lg font-medium ${STATUS_COLORS[q.status] || 'bg-slate-500/15 text-slate-400'}`}>
                                                    {q.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-slate-400">{q.valid_until || '—'}</td>
                                            <td className="px-4 py-3 text-slate-500 text-xs">{q.created_by}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                                                    <button
                                                        onClick={() => setExportQuot(q)}
                                                        className="text-slate-400 hover:text-green-400 transition p-1"
                                                        title="Export"
                                                    >
                                                        <Download size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => setModal(q)}
                                                        className="text-slate-400 hover:text-blue-400 transition p-1"
                                                        title="Edit"
                                                    >
                                                        <Edit2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ModalOverlay show={!!modal}>
                {modal && (
                    <QuotModal
                        quotation={modal === 'new' || modal === 'new-proforma' ? null : modal}
                        isProforma={modal === 'new-proforma' || (modal !== 'new' && !!modal?.proforma_number)}
                        profile={profile}
                        onClose={() => setModal(null)}
                        onSave={data => (modal === 'new' || modal === 'new-proforma') ? createMut.mutate(data) : updateMut.mutate({ id: modal.id, ...data })}
                    />
                )}
            </ModalOverlay>
            )}

            <ModalOverlay show={!!exportQuot}>
                {exportQuot && (
                    <ExportModal quotation={exportQuot} onClose={() => setExportQuot(null)} />
                )}
            </ModalOverlay>
        </StaffLayout>
    )
}
